import {
    Form,
    FormField,
    FormFieldConditions,
    FormSection,
    FormType,
    DropdownData,
    InputType,
    ReactiveFormChangedField,
    ReactiveFormValue,
} from '@fitogest/reactive-form-builder/interfaces';
import { list, ReactiveSelectionResolver } from '@fitogest/reactive-form-builder/utils';
import { FincasModel } from 'src/app/models/form-common/fincas.module';
import { ParcelasModel } from 'src/app/models/form-common/parcelas.model';
import { VisitasSemanaModel } from 'src/app/models/visitas/visitas-semana.model';

const FIRST_WEEK = 1;
const LAST_WEEK = 54;
const FIRST_YEAR = 2020;
const LAST_YEAR = 2030;
const MAX_PEST_ROWS = 6;

export class VisitasSemanaListsDefinition {
    public fincas: DropdownData<FincasModel> = list<FincasModel>(['...', null]);

    public parcelas: DropdownData<ParcelasModel> = list<ParcelasModel>(['...', null]);

    public semanas: DropdownData<number> = list<number>(
        ['...', null],
        ...Array.from({ length: LAST_WEEK - FIRST_WEEK + 1 }, (_item, index) => FIRST_WEEK + index),
    );

    public anos: DropdownData<number> = list<number>(
        ['...', null],
        ...Array.from({ length: LAST_YEAR - FIRST_YEAR + 1 }, (_item, index) => FIRST_YEAR + index),
    );

    public plagas: DropdownData<string> = list<string>(
        ['...', null],
        ...[
            'Araña roja',
            'Trip',
            'Empoasca',
            'Pulgón',
            'Pulgón de la madera',
            'Cotonet',
            'Mosca blanca',
            'Piojo san José',
            'Típula',
            'Otiorrinco',
            'Caparreta',
            'Mosquito del ciruelo',
            'Acaro blanco',
            'Eriófidos',
            'Orugas',
            'Ceratitits',
            'Escarabajo flor',
            'Escarabajo como flor',
            'Barrenillo del almendro',
            'Sciara',
            'Arañuelo',
            'Minador',
            'Cabezudo o Psila',
            'Caracol/Babosa',
            'Anarsia',
            'Molesta',
            'Fauna auxiliar',
            'Oidio',
            'Roya',
            'Botritis',
            'Repilo',
            'Bolladura',
            'Apomyelois',
            'Cryptoblabes',
            'Scatella',
        ].sort(),
    );

    public umbrales: DropdownData<string> = list<string>(
        ['...', null],
        '< 3 Ind/brote',
        '> 3 Ind/brote',
        '% Elevado árboles afectados',
        '% Medio árboles afectados',
        '% Bajo árboles afectados',
        'Fruta atacada',
    );
}

export class VisitasSemanaFormDefinition {
    private readonly formType!: FormType;

    public readonly lists: VisitasSemanaListsDefinition = new VisitasSemanaListsDefinition();

    public model: ReactiveFormValue<VisitasSemanaModel> = {} as ReactiveFormValue<VisitasSemanaModel>;

    public lastChangedFieldName: ReactiveFormChangedField<VisitasSemanaModel> = '';

    public formFields!: Form<VisitasSemanaModel>;

    public fieldNames: FormField<VisitasSemanaModel>[] = [];

    public fieldLabels: string[] = [];

    /**
     * Creates the weekly visit form definition shared by edit forms and list modals.
     *
     * The definition centralizes visit fields, dropdown lists and hidden controls so
     * all weekly visit modes use the same source of truth.
     *
     * @constructor
     * @public
     *
     * @param type - Request type used to define the form behaviour.
     * @param model - Optional weekly visit model used as initial form data.
     * @param table - Optional table configuration used by table-based form modes.
     *
     * @returns A new weekly visit form definition instance.
     */
    constructor(
        type: FormType,
        model: Partial<VisitasSemanaModel> | null = {},
        table: (string[] | string[][])[] | null = null,
    ) {
        this.formType = +type;
        this.model = {
            ...this.model,
            ...(model ?? {}),
        } as ReactiveFormValue<VisitasSemanaModel>;
        this.normalizeModel();
        this.formFields = this.buildForm(table);
    }// constructor();

    /**
     * Updates the stored model and rebuilds visible section metadata.
     *
     * @public
     *
     * @param model - Partial weekly visit model received from the reactive form.
     * @param lastChangedFieldName - Last field changed by the user or by the builder.
     *
     * @returns Nothing.
     */
    public updateModel(
        model: ReactiveFormValue<VisitasSemanaModel>,
        lastChangedFieldName: ReactiveFormChangedField<VisitasSemanaModel>,
    ): void {
        this.model = {
            ...this.model,
            ...model,
        } as ReactiveFormValue<VisitasSemanaModel>;
        this.lastChangedFieldName = lastChangedFieldName;

        if (lastChangedFieldName === 'id_finca') {
            this.clearInvalidParcelSelection();
        }// if();

        this.normalizeModel();
        this.refreshSections();
    }// updateModel();

    /**
     * Rebuilds section visibility and field metadata without replacing dropdown lists.
     *
     * @public
     *
     * @returns Nothing.
     */
    public refreshSections(): void {
        this.fieldNames = [];
        this.fieldLabels = [];
        this.formFields = {
            ...this.formFields,
            sections: this.getFormSections(),
        };
    }// refreshSections();

    /**
     * Builds and returns the main weekly visit fields.
     *
     * @public
     *
     * @returns The weekly visit fields sorted by display order.
     */
    public getVisitFields(): FormField<VisitasSemanaModel>[] {
        const visitFields: FormField<VisitasSemanaModel>[] = [
            this.farmField(),
            this.parcelField(),
            this.dropdownField('semana', 'Semana', 3, this.lists.semanas),
            this.dropdownField('ano', 'Año', 4, this.lists.anos),
        ];

        for (let index = 1; index <= MAX_PEST_ROWS; index += 1) {
            const baseOrder = 4 + ((index - 1) * 4);

            visitFields.push(this.dropdownField(`plaga${index}`, `Plaga #${index}`, baseOrder + 1, this.lists.plagas));
            visitFields.push(this.dropdownField(`umbral${index}`, `Umbral #${index}`, baseOrder + 2, this.lists.umbrales));
            visitFields.push(this.numericField(`cantidad${index}`, `Cantidad #${index}`, baseOrder + 3));
            visitFields.push(this.textAreaField(`observaciones${index}`, `Observaciones #${index}`, baseOrder + 4));
        }// for();

        this.appendVisibleFields(visitFields);
        return visitFields.sort((first, second) => (first.order ?? 0) - (second.order ?? 0));
    }// getVisitFields();

    /**
     * Builds hidden fields required by submit and delete actions.
     *
     * @public
     *
     * @returns The hidden fields sorted by display order.
     */
    public getHiddenFields(): FormField<VisitasSemanaModel>[] {
        const hiddenFields: FormField<VisitasSemanaModel>[] = [
            {
                name: 'id',
                label: '',
                input: {
                    type: InputType.HIDDEN,
                    isEditable: false,
                    defaultValue: this.getHiddenFieldDefaultValue('id'),
                },
                width: 0,
                conditions: this.conditions(),
                order: 1,
            },
        ];

        return hiddenFields.sort((first, second) => (first.order ?? 0) - (second.order ?? 0));
    }// getHiddenFields();

    /**
     * Builds the finca dropdown field for editable forms or the display field for modals.
     *
     * @private
     *
     * @returns Finca field definition.
     */
    private farmField(): FormField<VisitasSemanaModel> {
        return {
            name: this.getFarmFieldName(),
            label: 'Agrupación',
            input: this.isEditable()
                ? {
                    type: InputType.DROPDOWN_SEARCH,
                    isEditable: true,
                    data: this.lists.fincas as DropdownData<unknown>,
                    dropdownConfiguration: { valuePrimaryKey: 'id' },
                    optionLabel: 'label',
                    optionValue: 'value',
                }
                : { type: InputType.ONLY_TEXT, isEditable: false },
            width: 3,
            conditions: this.conditions({ isRequired: this.isEditable() }),
            order: 1,
        };
    }// farmField();

    /**
     * Builds the parcel dropdown field for editable forms or the display field for modals.
     *
     * @private
     *
     * @returns Parcel field definition.
     */
    private parcelField(): FormField<VisitasSemanaModel> {
        return {
            name: this.getParcelFieldName(),
            label: 'Subparcela',
            input: this.isEditable()
                ? {
                    type: InputType.DROPDOWN_SEARCH,
                    isEditable: true,
                    data: this.lists.parcelas as DropdownData<unknown>,
                    dropdownConfiguration: {
                        valuePrimaryKey: 'id',
                        filter: (option: { value: unknown }): boolean => this.filterParcelOption(option.value as ParcelasModel | null),
                    },
                    optionLabel: 'label',
                    optionValue: 'value',
                }
                : { type: InputType.ONLY_TEXT, isEditable: false },
            dependencyBehaviour: {
                clearOnParentChange: false,
                keepValueUntilOptionsLoaded: true,
            },
            width: 3,
            conditions: this.conditions({ isRequired: this.isEditable() }),
            order: 2,
        };
    }// parcelField();

    /**
     * Builds a dropdown field using the project defaults.
     *
     * @private
     *
     * @param name - Backend field name.
     * @param label - Visible field label.
     * @param order - Display order.
     * @param data - Dropdown values.
     *
     * @returns Dropdown field definition.
     */
    private dropdownField<TValue>(
        name: string,
        label: string,
        order: number,
        data: DropdownData<TValue>,
    ): FormField<VisitasSemanaModel> {
        return {
            name,
            label,
            input: this.isEditable()
                ? {
                    type: InputType.DROPDOWN,
                    isEditable: true,
                    data: data as DropdownData<unknown>,
                    optionLabel: 'label',
                    optionValue: 'value',
                }
                : { type: InputType.ONLY_TEXT, isEditable: false },
            width: 3,
            conditions: this.conditions(),
            order,
        };
    }// dropdownField();

    /**
     * Builds a numeric field using the project defaults.
     *
     * @private
     *
     * @param name - Backend field name.
     * @param label - Visible field label.
     * @param order - Display order.
     *
     * @returns Numeric field definition.
     */
    private numericField(name: string, label: string, order: number): FormField<VisitasSemanaModel> {
        return {
            name,
            label,
            input: { type: this.isEditable() ? InputType.NUMERIC : InputType.ONLY_TEXT, isEditable: this.isEditable() },
            width: 3,
            conditions: this.conditions(),
            order,
        };
    }// numericField();

    /**
     * Builds a text area field using the project defaults.
     *
     * @private
     *
     * @param name - Backend field name.
     * @param label - Visible field label.
     * @param order - Display order.
     *
     * @returns Text area field definition.
     */
    private textAreaField(name: string, label: string, order: number): FormField<VisitasSemanaModel> {
        return {
            name,
            label,
            input: {
                type: this.isEditable() ? InputType.TEXTAREA : InputType.ONLY_TEXT,
                isEditable: this.isEditable(),
                textAreaConfiguration: { hasVoiceTranscription: false, canTakePhoto: false, photoField: undefined },
            },
            width: 3,
            conditions: this.conditions(),
            order,
        };
    }// textAreaField();

    /**
     * Builds and returns all weekly visit form sections.
     *
     * @private
     *
     * @returns The form sections sorted by display order.
     */
    private getFormSections(): FormSection<VisitasSemanaModel>[] {
        const sections: FormSection<VisitasSemanaModel>[] = [
            {
                name: 'Datos de visita',
                fields: this.getVisitFields(),
                width: 12,
                isVisible: true,
                order: 1,
            },
            {
                name: '',
                fields: this.getHiddenFields(),
                width: 12,
                isVisible: true,
                order: 2,
            },
        ];

        return sections.sort((first, second) => (first.order ?? 0) - (second.order ?? 0));
    }// getFormSections();

    /**
     * Builds the root form metadata object.
     *
     * @private
     *
     * @param table - Optional table configuration used by table-based form modes.
     *
     * @returns The root form metadata object.
     */
    private buildForm(table: (string[] | string[][])[] | null): Form<VisitasSemanaModel> {
        return {
            name: 'visitas-semana',
            header: this.getHeader(),
            type: this.formType,
            sections: this.getFormSections(),
            table: table === null
                ? null
                : { title: '', showIndex: false, data: table?.[0], style: table?.[1] },
            uploadButton: { isEnabled: false, label: '' },
            uploadButtons: [],
            buttons: [],
            signed: false,
            dropdownCatalogs: {
                critical: [this.getFarmFieldName(), this.getParcelFieldName()],
                deferred: [],
                lazy: [],
            },
            previousDataTransform: (previousData: VisitasSemanaModel): VisitasSemanaModel => ({ ...previousData }),
        };
    }// buildForm();

    /**
     * Creates field conditions using the project defaults.
     *
     * @private
     *
     * @param overrides - Partial condition values to override defaults.
     *
     * @returns A complete field conditions object.
     */
    private conditions(overrides: Partial<FormFieldConditions> = {}): FormFieldConditions {
        return {
            isRememberedOnDuplicate: true,
            isRequired: false,
            isRetained: false,
            hasUndoEnabled: false,
            isVisible: true,
            ...overrides,
        };
    }// conditions();

    /**
     * Appends visible fields to table/export metadata arrays.
     *
     * @private
     *
     * @param fields - Fields to inspect and append when visible.
     *
     * @returns Nothing.
     */
    private appendVisibleFields(fields: FormField<VisitasSemanaModel>[]): void {
        const visibleFields = fields.filter((field) =>
            field.conditions.isVisible && field.input.type !== InputType.EMPTY && field.input.type !== InputType.HIDDEN,
        );

        this.fieldNames = this.fieldNames.concat(visibleFields);
        this.fieldLabels = this.fieldLabels.concat(visibleFields.map((field) => field.label));
    }// appendVisibleFields();

    /**
     * Returns the form header based on the active form mode.
     *
     * @private
     *
     * @returns Human-readable form header.
     */
    private getHeader(): string {
        switch (this.formType) {
            case FormType.CREATION:
                return 'Crear Visita';
            case FormType.DUPLICATION:
                return 'Duplicar Visita';
            case FormType.EDITION:
                return 'Actualizar Visita';
            case FormType.DELETION:
            case FormType.INFORMATIVE:
                return 'Datos de la visita semanal';
            default:
                return '';
        }// switch();
    }// getHeader();

    /**
     * Indicates whether normal fields can be edited in the current form mode.
     *
     * @private
     *
     * @returns True for creation, edition and duplication modes.
     */
    private isEditable(): boolean {
        return this.formType < FormType.DELETION;
    }// isEditable();

    /**
     * Returns the field name used for the farm depending on the form mode.
     *
     * @private
     *
     * @returns Backend id field in editable forms, display field in modal forms.
     */
    private getFarmFieldName(): string {
        return this.isEditable() ? 'id_finca' : 'finca_nombre';
    }// getFarmFieldName();

    /**
     * Returns the field name used for the parcel depending on the form mode.
     *
     * @private
     *
     * @returns Backend id field in editable forms, display field in modal forms.
     */
    private getParcelFieldName(): string {
        return this.isEditable() ? 'id_parcela' : 'parcela_nombre';
    }// getParcelFieldName();

    /**
     * Filters parcel options by the selected finca.
     *
     * @private
     *
     * @param parcel - Parcel option value.
     *
     * @returns True when the parcel must remain visible.
     */
    private filterParcelOption(parcel: ParcelasModel | null): boolean {
        const selectedFarmId = this.getSelectedFarmId();

        if (!selectedFarmId || !parcel) {
            return true;
        }// if();

        return selectedFarmId === this.normalizeIdentifier(parcel.id_finca);
    }// filterParcelOption();

    /**
     * Clears the selected parcel only when it does not belong to the selected finca.
     *
     * @private
     *
     * @returns Nothing.
     */
    private clearInvalidParcelSelection(): void {
        const selectedFarmId = this.getSelectedFarmId();

        if (!selectedFarmId) {
            this.model.id_parcela = null;
            return;
        }// if();

        const selectedParcelId = this.normalizeIdentifier(this.model.id_parcela);

        if (!selectedParcelId) {
            return;
        }// if();

        const selectedParcel = this.getSelectedParcel(selectedParcelId);

        if (!selectedParcel) {
            if (this.hasLoadedParcels()) {
                this.model.id_parcela = null;
            }// if();

            return;
        }// if();

        if (selectedFarmId !== this.normalizeIdentifier(selectedParcel.id_finca)) {
            this.model.id_parcela = null;
        }// if();
    }// clearInvalidParcelSelection();

    /**
     * Returns the currently selected farm id from the reactive model.
     *
     * @private
     *
     * @returns Selected farm id or null.
     */
    private getSelectedFarmId(): string | null {
        return this.normalizeIdentifier(this.model.id_finca);
    }// getSelectedFarmId();

    /**
     * Resolves the selected parcel from the model and loaded dropdown values.
     *
     * @private
     *
     * @param selectedParcelId - Parcel id to resolve.
     *
     * @returns Matching parcel or null.
     */
    private getSelectedParcel(selectedParcelId: string): ParcelasModel | null {
        const currentValue = (this.model as Record<string, unknown>)['id_parcela'];

        return ReactiveSelectionResolver.resolveSelectedEntities(this.lists.parcelas, currentValue)[0]
            ?? ReactiveSelectionResolver.resolveValueById(this.lists.parcelas, selectedParcelId);
    }// getSelectedParcel();

    /**
     * Indicates whether parcel options have been loaded from the backend.
     *
     * @private
     *
     * @returns True when at least one real parcel option exists.
     */
    private hasLoadedParcels(): boolean {
        return this.lists.parcelas.values.some((option) => option.value !== null && option.value !== undefined);
    }// hasLoadedParcels();

    /**
     * Normalizes fields that are sensitive to backend display formats.
     *
     * @private
     *
     * @returns Nothing.
     */
    private normalizeModel(): void {
        this.normalizeNumericDropdownValue('semana');
        this.normalizeNumericDropdownValue('ano');
    }// normalizeModel();

    /**
     * Converts numeric dropdown values returned as strings into numbers.
     *
     * @private
     *
     * @param fieldName - Field to normalize.
     *
     * @returns Nothing.
     */
    private normalizeNumericDropdownValue(fieldName: 'semana' | 'ano'): void {
        const modelRecord = this.model as Record<string, unknown>;
        const value = modelRecord[fieldName];

        if (value === null || value === undefined || value === '') {
            modelRecord[fieldName] = null;
            return;
        }// if();

        const numericValue = Number(value);

        if (Number.isNaN(numericValue) || numericValue === 0) {
            modelRecord[fieldName] = null;
            return;
        }// if();

        modelRecord[fieldName] = numericValue;
    }// normalizeNumericDropdownValue();

    /**
     * Reads a scalar hidden field default value from the current model.
     *
     * @private
     *
     * @param name - Hidden field name.
     *
     * @returns The default value for the hidden field.
     */
    private getHiddenFieldDefaultValue(name: string): string | number | boolean | null | undefined {
        const value = (this.model as Record<string, unknown>)[name];

        if (value === null || value === undefined) {
            return null;
        }// if();

        if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
            return value;
        }// if();

        return String(value);
    }// getHiddenFieldDefaultValue();

    /**
     * Normalizes scalar ids and dropdown option objects into comparable ids.
     *
     * @private
     *
     * @param value - Raw value to inspect.
     *
     * @returns Normalized id or null.
     */
    private normalizeIdentifier(value: unknown): string | null {
        return ReactiveSelectionResolver.normalizeId(value);
    }// normalizeIdentifier();
}// class;
